import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { Loader2, ArrowLeft } from 'lucide-react'
import GlassCard from '@/components/GlassCard'
import EmptyIllustration from '@/components/EmptyIllustration'

interface AuditRunningCardProps {
  message: string
  progress: number
  onCancel?: () => void
}

/** The in-progress card shared by hosted (/audit/:id) and local (/audit-local) runs. */
export function AuditRunningCard({ message, progress, onCancel }: AuditRunningCardProps) {
  return (
    <GlassCard className="text-center space-y-6">
      <div className="flex justify-center">
        <Loader2 size={40} className="animate-spin text-accent" />
      </div>
      <div>
        <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
          {message || 'Preparing audit…'}
        </p>
        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden max-w-xs mx-auto">
          <motion.div
            className="h-full rounded-full"
            style={{ background: 'var(--accent)' }}
            animate={{ width: `${progress}%` }}
            transition={{ ease: 'linear', duration: 0.3 }}
          />
        </div>
        <p className="text-xs mt-2 tabular-nums" style={{ color: 'var(--text-muted)' }}>
          {Math.round(progress)}%
        </p>
      </div>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          Cancel
        </button>
      )}
    </GlassCard>
  )
}

interface AuditErrorCardProps {
  message: string
  actionLabel: string
  onAction: () => void
  children?: ReactNode
}

/** The failure card shared by hosted and local runs. */
export function AuditErrorCard({ message, actionLabel, onAction, children }: AuditErrorCardProps) {
  return (
    <GlassCard className="text-center space-y-4 py-10">
      <EmptyIllustration variant="error" size={104} className="mx-auto text-[var(--text-secondary)]" />
      <p className="text-red-400 font-medium">{message}</p>
      {children}
      <button
        onClick={onAction}
        className="flex items-center gap-2 mx-auto text-sm transition-colors"
        style={{ color: 'var(--text-secondary)' }}
      >
        <ArrowLeft size={14} /> {actionLabel}
      </button>
    </GlassCard>
  )
}
